import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Settings, BookOpen, Users, Calendar, MessageSquare, DollarSign, Video, Upload, Mail, LayoutDashboard, Library, FileText } from 'lucide-react';

// Student Tour Steps
const studentTourSteps = [
  {
    id: 'welcome',
    title: 'Welcome to Your Dashboard! 🎉',
    content: 'This is your central hub. Here you can see your progress, upcoming appointments, and quick access to all features.',
    icon: LayoutDashboard,
    position: 'center'
  },
  {
    id: 'courses',
    title: 'Your Courses 📚',
    content: 'Access all your enrolled courses here. Track your progress, watch lessons, and complete assignments. Each course is designed to build your spiritual foundation.',
    icon: BookOpen,
    position: 'center'
  },
  {
    id: 'library',
    title: 'Resource Library 📖',
    content: 'Explore our extensive library of ebooks and resources. As a mentorship member, you get free access to premium content! Click "Read Now" to open the secure reader.',
    icon: Library,
    position: 'center'
  },
  {
    id: 'appointments',
    title: 'Book Appointments 📅',
    content: 'Schedule your monthly mentorship session here. You can book one session per calendar month with Pastor Obed for personalized guidance.',
    icon: Calendar,
    position: 'center'
  },
  {
    id: 'community',
    title: 'Join the Community 💬',
    content: 'Connect with fellow mentees, ask questions, and share insights. Use @mentions to tag others and check pinned messages from mentors.',
    icon: MessageSquare,
    position: 'center'
  },
  {
    id: 'ai-assistant',
    title: 'Meet The Vine Voice 🤖',
    content: 'Look for the purple AI button at the bottom right! Ask "The Vine Voice" anything about courses, appointments, or get help navigating the platform.',
    icon: Sparkles,
    position: 'center'
  },
  {
    id: 'complete',
    title: "You're All Set! ✨",
    content: "You now know the essentials. Explore at your own pace, and don't hesitate to reach out if you need help. God bless your journey!",
    icon: Sparkles,
    position: 'center'
  }
];

// Admin Tour Steps
const adminTourSteps = [
  {
    id: 'admin-welcome',
    title: 'Welcome to the Admin Panel! ⚙️',
    content: 'As an admin, you have full control over the platform. This tour will guide you through all the powerful features at your disposal.',
    icon: Settings,
    position: 'center'
  },
  {
    id: 'welcome-video',
    title: 'Welcome Video Management 🎬',
    content: 'Set a personal welcome video for your students. Simply paste a YouTube, Vimeo, or Google Drive video URL and click "Save Welcome Video".',
    icon: Video,
    position: 'center'
  },
  {
    id: 'courses-lessons',
    title: 'Courses & Lessons 📚',
    content: 'Create and manage courses here. Click "Add Course" to create new ones, expand a course to add lessons. Each lesson can have a video from Google Drive.',
    icon: BookOpen,
    position: 'center'
  },
  {
    id: 'ebooks',
    title: 'eBook Library 📖',
    content: 'Upload and manage ebooks. Click "Add Ebook" to add new resources. Set premium status to restrict access to mentorship members.',
    icon: FileText,
    position: 'center'
  },
  {
    id: 'file-uploads',
    title: 'File Uploads 📁',
    content: 'View all files uploaded by students including homework and assignments. Download, review, and manage submitted work here.',
    icon: Upload,
    position: 'center'
  },
  {
    id: 'user-management',
    title: 'User Management 👥',
    content: 'View all registered users, their roles, membership tiers, and enrollment status. Click on a user to see their details.',
    icon: Users,
    position: 'center'
  },
  {
    id: 'payments',
    title: 'Payment Tracking 💰',
    content: 'Click the "Payments" tab to view all transactions. See total revenue, completed payments, and manage enrollments. Export data for accounting.',
    icon: DollarSign,
    position: 'center'
  },
  {
    id: 'email-campaigns',
    title: 'Email Campaigns 📧',
    content: 'Send announcements and newsletters to your users. Select recipients by membership tier or send to everyone at once.',
    icon: Mail,
    position: 'center'
  },
  {
    id: 'mentor-portal',
    title: 'Mentor Portal 👨‍🏫',
    content: 'Access the Mentor Portal from the header to view analytics, track mentee progress, manage appointments, and send bulk communications.',
    icon: Users,
    position: 'center'
  },
  {
    id: 'admin-complete',
    title: 'You\'re Ready to Manage! 🚀',
    content: 'You now know all the admin features. Remember to use "The Vine Voice" AI - it can help you navigate and answer questions about any feature!',
    icon: Sparkles,
    position: 'center'
  }
];

// Mentor Tour Steps
const mentorTourSteps = [
  {
    id: 'mentor-welcome',
    title: 'Welcome to the Mentor Portal! 👨‍🏫',
    content: 'This dashboard gives you complete visibility into your mentees\' progress and powerful tools to support their growth.',
    icon: Users,
    position: 'center'
  },
  {
    id: 'analytics',
    title: 'Analytics Overview 📊',
    content: 'View key metrics at a glance: total mentees, active students, course completion rates, and engagement scores.',
    icon: LayoutDashboard,
    position: 'center'
  },
  {
    id: 'mentee-list',
    title: 'Mentee Management 👥',
    content: 'Browse all your mentees, filter by track or tier, and search by name. Click on any mentee to view their detailed progress.',
    icon: Users,
    position: 'center'
  },
  {
    id: 'appointments-mentor',
    title: 'Appointment Management 📅',
    content: 'View and manage all booked appointments. Approve, reschedule, or cancel sessions as needed.',
    icon: Calendar,
    position: 'center'
  },
  {
    id: 'bulk-actions',
    title: 'Bulk Communications 📧',
    content: 'Send emails or messages to groups of mentees. Filter by cohort, track, or tier for targeted communication.',
    icon: Mail,
    position: 'center'
  },
  {
    id: 'mentor-complete',
    title: 'Start Mentoring! ✨',
    content: 'You\'re equipped to guide your mentees effectively. Check the AI assistant for help with any feature!',
    icon: Sparkles,
    position: 'center'
  }
];

// Tour Type Selection Modal
export const TourSelector = ({ isOpen, onClose, onSelectTour, userRole }) => {
  if (!isOpen) return null;

  const tourOptions = [
    {
      id: 'student',
      title: 'Student Tour',
      description: 'Learn how to navigate courses, library, community, and appointments',
      icon: BookOpen,
      color: 'purple',
      available: true
    },
    {
      id: 'admin',
      title: 'Admin Tour',
      description: 'Master the admin panel: courses, users, payments, and content management',
      icon: Settings,
      color: 'orange',
      available: userRole === 'admin' || userRole === 'mentor'
    },
    {
      id: 'mentor',
      title: 'Mentor Tour',
      description: 'Explore mentee tracking, analytics, and communication tools',
      icon: Users,
      color: 'blue',
      available: userRole === 'admin' || userRole === 'mentor'
    }
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Sparkles className="w-5 h-5 mr-2" />
              <span className="text-lg font-semibold">Choose Your Tour</span>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          <p className="text-gray-600 text-sm mb-4">
            Select a tour to learn about specific features of the platform.
          </p>
          
          {tourOptions.map((tour) => {
            const Icon = tour.icon;
            return (
              <button
                key={tour.id}
                onClick={() => tour.available && onSelectTour(tour.id)}
                disabled={!tour.available}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  tour.available
                    ? `border-gray-200 hover:border-${tour.color}-400 hover:bg-${tour.color}-50 cursor-pointer`
                    : 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                }`}
                data-testid={`tour-option-${tour.id}`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-xl ${
                    tour.color === 'purple' ? 'bg-purple-100' :
                    tour.color === 'orange' ? 'bg-orange-100' : 'bg-blue-100'
                  }`}>
                    <Icon className={`w-6 h-6 ${
                      tour.color === 'purple' ? 'text-purple-600' :
                      tour.color === 'orange' ? 'text-orange-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{tour.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{tour.description}</p>
                    {!tour.available && (
                      <p className="text-xs text-orange-600 mt-2">Requires admin/mentor access</p>
                    )}
                  </div>
                  {tour.available && (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Main Tour Component
const TourSystem = ({ tourType, showTour, setShowTour, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const tourSteps = tourType === 'admin' ? adminTourSteps :
                    tourType === 'mentor' ? mentorTourSteps : studentTourSteps;
  
  const tourKey = `${tourType}_tour_completed`;

  // Reset step when tour opens or type changes - using ref to avoid effect
  const prevShowTour = React.useRef(showTour);
  const prevTourType = React.useRef(tourType);
  
  if (showTour && (!prevShowTour.current || tourType !== prevTourType.current)) {
    if (currentStep !== 0) {
      setCurrentStep(0);
    }
  }
  prevShowTour.current = showTour;
  prevTourType.current = tourType;

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeTour = () => {
    localStorage.setItem(tourKey, 'true');
    setShowTour(false);
    setCurrentStep(0);
    if (onComplete) onComplete();
  };

  const skipTour = () => {
    localStorage.setItem(tourKey, 'true');
    setShowTour(false);
    setCurrentStep(0);
  };

  if (!showTour) return null;

  const step = tourSteps[currentStep];
  const StepIcon = step.icon;

  const getTourTheme = () => {
    switch (tourType) {
      case 'admin':
        return {
          gradient: 'from-orange-500 to-orange-600',
          accent: 'orange',
          progressBg: 'from-orange-500 to-red-500'
        };
      case 'mentor':
        return {
          gradient: 'from-blue-500 to-blue-600',
          accent: 'blue',
          progressBg: 'from-blue-500 to-purple-500'
        };
      default:
        return {
          gradient: 'from-purple-600 to-purple-700',
          accent: 'purple',
          progressBg: 'from-purple-600 to-orange-500'
        };
    }
  };

  const theme = getTourTheme();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={skipTour} />
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Progress Bar */}
        <div className="h-1 bg-gray-100">
          <div 
            className={`h-full bg-gradient-to-r ${theme.progressBg} transition-all duration-300`}
            style={{ width: `${((currentStep + 1) / tourSteps.length) * 100}%` }}
          />
        </div>

        {/* Header */}
        <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-4 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <StepIcon className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium capitalize">{tourType} Tour</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-white/80">
                {currentStep + 1} of {tourSteps.length}
              </span>
              <button 
                onClick={skipTour}
                className="text-white/80 hover:text-white transition-colors ml-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className={`p-3 rounded-xl bg-${theme.accent}-100`}>
              <StepIcon className={`w-8 h-8 text-${theme.accent}-600`} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{step.title}</h2>
          </div>
          <p className="text-gray-600 leading-relaxed">{step.content}</p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {tourSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep 
                    ? `bg-${theme.accent}-600 w-6` 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
          
          <div className="flex items-center space-x-3">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className={`flex items-center px-5 py-2 bg-${theme.accent}-600 text-white rounded-lg hover:bg-${theme.accent}-700 transition-colors font-medium`}
              data-testid="tour-next-btn"
            >
              {currentStep === tourSteps.length - 1 ? (
                "Get Started"
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Tour Button with role awareness
export const EnhancedTourButton = ({ onClick, variant = 'default' }) => {
  const buttonStyles = {
    default: 'text-purple-600 hover:text-purple-700 hover:bg-purple-50',
    admin: 'text-orange-600 hover:text-orange-700 hover:bg-orange-50',
    compact: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center px-3 py-1.5 text-sm rounded-lg transition-colors ${buttonStyles[variant]}`}
      data-testid="take-tour-btn"
    >
      <Sparkles className="w-4 h-4 mr-1.5" />
      Take Tour
    </button>
  );
};

export default TourSystem;
